import { useEffect, useState } from 'react'
import './App.css'
import { findPage, getPageFaq, pageByPath, pageSeoDetails, pages, type SitePage } from './site-data'

const CTA_URL = '/go/'

const imageSource = (image: SitePage['image']) => image === 'background'
  ? '/images/background-before-after.webp'
  : image === 'gallery'
    ? '/images/creative-output-gallery.webp'
    : '/images/hero-collage.webp'

const pageScenarios: Record<string, string[]> = {
  '/nejroset-dlya-prezentacij/': ['Учебный доклад', 'Коммерческое предложение', 'Отчёт команды', 'План выступления'],
  '/nejroset-dlya-foto/': ['Фото для публикации', 'Карточка товара', 'Портрет', 'Изображение для презентации'],
  '/nejroset-dlya-izobrazhenij/': ['Иллюстрация к статье', 'Концепт рекламного визуала', 'Фон для публикации', 'Обложка проекта'],
  '/generator-izobrazhenij/': ['Предметная сцена', 'Абстрактная композиция', 'Редакционная иллюстрация', 'Визуал для соцсетей'],
  '/zamenit-fon-na-foto/': ['Карточка товара', 'Деловой портрет', 'Анонс события', 'Публикация в соцсетях'],
  '/udalit-fon/': ['Каталог товаров', 'Коллаж', 'Презентация', 'Баннер'],
  '/dobavit-udalit-obekt-na-foto/': ['Очистить кадр', 'Дополнить композицию', 'Исправить деталь', 'Подготовить рекламу'],
  '/obedinit-foto/': ['Сравнение до и после', 'Фотоколлаж', 'Подборка товаров', 'История события'],
  '/ozhivit-foto/': ['История для соцсетей', 'Динамичная обложка', 'Анонс', 'Слайд презентации'],
  '/sozdat-dizajn/': ['Публикация', 'Афиша', 'Презентация', 'Карточка товара'],
  '/redaktor-foto/': ['Кадрирование', 'Свет и цвет', 'Текст поверх фото', 'Подготовка формата'],
  '/foto/': ['Обработка снимка', 'Работа с фоном', 'Коллаж', 'Анимация кадра'],
  '/ai/': ['Генерация изображения', 'Изменение фото', 'Подготовка слайдов', 'Варианты дизайна'],
  '/dizajn/': ['Презентация', 'Публикация', 'Афиша', 'Рекламный макет'],
}

const proofMode = (page: SitePage) => {
  if (page.path.includes('prezentacij')) return 'slides'
  if (page.path.includes('obedinit')) return 'collage'
  if (page.path.includes('ozhivit')) return 'motion'
  if (page.path.includes('generator') || page.path.includes('izobrazhenij') || page.path === '/ai/') return 'prompt'
  if (page.path.includes('fon') || page.path.includes('obekt') || page.path.includes('foto')) return 'photo'
  return 'design'
}

function ProductProof({ page }: { page: SitePage }) {
  const mode = proofMode(page)
  return (
    <div className={`product-proof proof-${mode}`} aria-label={`Схема работы инструмента «${page.navLabel}»`}>
      <div className="proof-toolbar">
        <span><i />Рабочий макет</span>
        <b>{page.navLabel}</b>
      </div>
      <div className="proof-stage">
        <div className="proof-source">
          <span>Исходник</span>
          <img src={imageSource(page.image)} width="560" height="380" alt="" loading="lazy" decoding="async" />
        </div>
        <div className="proof-operation">
          <span>Действие</span>
          <strong>{page.steps[1]}</strong>
          <div className="proof-control"><i /><i /><i /></div>
        </div>
        <div className="proof-result">
          <span>Результат</span>
          <div className="proof-result-canvas">
            <img src={imageSource(page.image)} width="560" height="380" alt="" loading="lazy" decoding="async" />
            <b>{page.steps[2]}</b>
          </div>
        </div>
      </div>
      <div className="proof-footer"><span>01&nbsp; {page.steps[0]}</span><span>02&nbsp; {page.steps[1]}</span><b>03&nbsp; {page.steps[2]}</b></div>
    </div>
  )
}

function Header() {
  const [open, setOpen] = useState(false)
  return (
    <header className="site-header">
      <div className="container nav-shell">
        <a className="brand" href="/" aria-label="Canva RU, главная">Canva</a>
        <button className="menu-toggle" type="button" aria-expanded={open} aria-controls="main-nav" onClick={() => setOpen(!open)}>
          <span /><span /><span /><span className="sr-only">Открыть меню</span>
        </button>
        <nav className={open ? 'main-nav is-open' : 'main-nav'} id="main-nav" aria-label="Основная навигация">
          <a href="/nejroset-dlya-prezentacij/">Презентации</a>
          <a href="/foto/">Фото</a>
          <a href="/ai/">ИИ</a>
          <a href="/dizajn/">Дизайн</a>
          <a href="/#tools">Возможности</a>
        </nav>
        <div className="nav-actions">
          <a className="button button-primary compact" href={CTA_URL} rel="nofollow">Попробовать</a>
        </div>
      </div>
    </header>
  )
}

function Footer() {
  const groups = [
    { title: 'Canva', links: ['/', '/sozdat-dizajn/', '/dizajn/'] },
    { title: 'Фото', links: ['/foto/', '/nejroset-dlya-foto/', '/redaktor-foto/', '/udalit-fon/', '/zamenit-fon-na-foto/', '/obedinit-foto/'] },
    { title: 'ИИ', links: ['/ai/', '/nejroset-dlya-izobrazhenij/', '/generator-izobrazhenij/', '/dobavit-udalit-obekt-na-foto/', '/ozhivit-foto/'] },
    { title: 'Презентации', links: ['/nejroset-dlya-prezentacij/'] },
  ]
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-about">
          <a className="brand brand-inverse" href="/">Canva</a>
        </div>
        {groups.map((group) => (
          <div className="footer-column" key={group.title}>
            <h2>{group.title}</h2>
            {group.links.map((path) => {
              const page = pageByPath.get(path)
              return page ? <a href={path} key={path}>{page.navLabel}</a> : null
            })}
          </div>
        ))}
      </div>
      <div className="container footer-bottom"><span>Canva RU</span><span>Инструменты собраны по задачам</span></div>
    </footer>
  )
}

function Hero({ page, compact = false }: { page: SitePage; compact?: boolean }) {
  return (
    <section className={compact ? 'hero feature-hero container' : 'hero container'}>
      <div className="hero-copy">
        <span className="eyebrow">{page.eyebrow}</span>
        <h1>{page.heading}</h1>
        <p>{page.intro}</p>
        <div className="hero-actions">
          <a className="button button-primary" href={CTA_URL} rel="nofollow">Создать дизайн</a>
          <a className="button button-ghost" href={CTA_URL} rel="nofollow">Открыть редактор</a>
        </div>
      </div>
      <div className={`hero-visual visual-${page.image}`}>
        <img src={imageSource(page.image)} width="720" height="520" alt={`Визуальный пример для страницы «${page.navLabel}»`} fetchPriority="high" decoding="async" />
        <div className="prompt-bar"><span>{page.image === 'background' ? 'Фон и объект разделены — можно продолжать' : page.steps[0]}</span><b>Готово</b></div>
      </div>
    </section>
  )
}

function ToolsMosaic() {
  const tools = [
    ['/nejroset-dlya-prezentacij/', 'Презентации из идеи', 'Задайте тему, получите структуру и настройте каждый слайд.', '/images/creative-output-gallery.webp'],
    ['/redaktor-foto/', 'Обработка фото', 'Кадрирование, фон, свет и детали в одном рабочем процессе.', '/images/background-before-after.webp'],
    ['/generator-izobrazhenij/', 'Изображения с ИИ', 'Опишите сюжет и сравните готовые визуальные направления.', '/images/hero-collage.webp'],
    ['/obedinit-foto/', 'Объединение фото', 'Соберите сравнение, коллаж или историю в одном макете.', '/images/creative-output-gallery.webp'],
  ]
  return (
    <section className="section surface" id="tools">
      <div className="container">
        <div className="section-heading"><span className="eyebrow">Возможности</span><h2>Всё нужное для визуальной задачи</h2><p>Начните с идеи, фотографии или пустого макета. Результат можно сразу доработать в редакторе.</p></div>
        <div className="mosaic">
          {tools.map(([path, title, description, image], index) => <a className={index === 0 ? 'mosaic-card mosaic-main' : index === 2 ? 'mosaic-card warm' : 'mosaic-card'} href={path} key={path}><div className="mosaic-copy"><span className="card-index">0{index + 1}</span><h3>{title}</h3><p>{description}</p></div><img className="mosaic-preview" src={image} width="560" height="360" alt="" loading="lazy" decoding="async" /></a>)}
        </div>
      </div>
    </section>
  )
}

function EditorShowcase() {
  return (
    <section className="section section-tint">
      <div className="container">
        <div className="section-heading"><span className="eyebrow">Рабочее пространство</span><h2>Редактор, в котором всё под рукой</h2><p>Шаблоны, изображения, текст и ИИ-инструменты собраны в одном пространстве.</p></div>
        <div className="editor-window" aria-label="Схематичный интерфейс редактора">
          <div className="editor-top"><b>Canva</b><span>Постер для новой коллекции</span><div><a href={CTA_URL} rel="nofollow">Поделиться</a><a className="editor-download" href={CTA_URL} rel="nofollow">Скачать</a></div></div>
          <aside><b>Инструменты</b><span className="active">Шаблоны</span><span>Элементы</span><span>Текст</span><span>Фото</span><span>ИИ-инструменты</span><span>Загрузки</span></aside>
          <div className="editor-canvas"><div className="poster"><small>НОВАЯ КОЛЛЕКЦИЯ</small><strong>Цвет задаёт<br />настроение</strong><img src="/images/hero-collage.webp" width="480" height="320" alt="Яркая подборка визуальных материалов" loading="lazy" decoding="async" /></div></div>
          <div className="editor-settings"><b>Настройки</b><label>Стиль<input value="Современный" readOnly /></label><label>Цвета<div className="color-row"><i /><i /><i /><i /></div></label><label>Размер<input value="Пост для соцсетей" readOnly /></label><div className="settings-note"><b>Макет готов</b><span>Текст и изображения можно изменить</span></div></div>
        </div>
      </div>
    </section>
  )
}

function FormatGallery() {
  return <section className="section surface"><div className="container"><div className="section-heading"><span className="eyebrow">Единый стиль</span><h2>Одна идея, разные форматы</h2><p>Соберите презентацию, публикацию, рекламу или приглашение в единой визуальной системе.</p></div><img className="wide-gallery" src="/images/creative-output-gallery.webp" width="1240" height="827" alt="Подборка визуальных материалов разных форматов" loading="lazy" decoding="async" /></div></section>
}

function Steps({ page = pages[0] }: { page?: SitePage }) {
  const notes = ['Начните с подходящего формата или собственного файла.', 'Настройте детали и сравните доступные варианты.', 'Проверьте макет и подготовьте нужный формат.']
  return (
    <section className="section" id="how-it-works"><div className="container"><div className="section-heading"><span className="eyebrow">Три шага</span><h2>От задачи к результату без лишних действий</h2><p>Каждый этап остаётся перед глазами, поэтому легко вернуться к настройкам и продолжить работу.</p></div><div className="step-grid">{page.steps.map((step, index) => <article className="step-card" key={step}><span className="step-number">0{index + 1}</span><div className={`step-art art-${index + 1}`}><b>{step}</b><span>{index === 0 ? 'Исходный материал' : index === 1 ? 'Рабочая настройка' : 'Готовый макет'}</span></div><h3>{step}</h3><p>{notes[index]}</p></article>)}</div></div></section>
  )
}

function BeforeAfter() {
  return <section className="section surface"><div className="container split-feature"><img src="/images/background-before-after.webp" width="690" height="460" alt="Пример фотографии до и после замены фона" loading="lazy" decoding="async" /><div><span className="eyebrow">Фон фотографии</span><h2>Меняйте фон, сохраняя главное</h2><p>Загрузите фотографию, выберите новый фон и продолжите работу с результатом в том же редакторе.</p><a className="button button-primary" href={CTA_URL} rel="nofollow">Заменить фон</a></div></div></section>
}

function RouteMatrix() {
  const groups = [
    { title: 'Фото', featured: true, paths: ['/nejroset-dlya-foto/', '/redaktor-foto/', '/udalit-fon/', '/zamenit-fon-na-foto/', '/obedinit-foto/'] },
    { title: 'ИИ', paths: ['/generator-izobrazhenij/', '/nejroset-dlya-izobrazhenij/', '/dobavit-udalit-obekt-na-foto/', '/ozhivit-foto/'] },
    { title: 'Дизайн', paths: ['/sozdat-dizajn/', '/nejroset-dlya-prezentacij/', '/dizajn/', '/'] },
  ]
  return <section className="section section-tint"><div className="container"><div className="section-heading"><span className="eyebrow">Все направления</span><h2>Найдите нужный инструмент</h2><p>Каждая задача ведёт на отдельную страницу с собственным сценарием, процессом и примерами результата.</p></div><div className="route-grid">{groups.map((group) => <article className={group.featured ? 'route-card featured' : 'route-card'} key={group.title}><h3>{group.title}</h3>{group.paths.map((path) => { const p = pageByPath.get(path); return p ? <a href={path} key={path}>{p.navLabel}</a> : null })}</article>)}</div></div></section>
}

function FeatureWorkbench({ page }: { page: SitePage }) {
  return <section className="section workbench-section"><div className="container workbench"><div className="workbench-media"><ProductProof page={page} /><div className="result-status"><b>Результат готов к редактированию</b><span>Можно менять текст, изображения и формат</span></div></div><div className="workbench-panel"><span className="eyebrow">Внутри редактора</span><h2>Весь процесс остаётся в одном макете</h2><p>{page.benefit}</p><div className="tool-list">{page.steps.map((step, index) => <div key={step}><span>{index + 1}</span><div><b>{step}</b><small>{index === 0 ? 'Добавьте исходные данные' : index === 1 ? 'Сравните настройки и варианты' : 'Продолжите работу с результатом'}</small></div></div>)}</div><a className="button button-primary" href={CTA_URL} rel="nofollow">Попробовать в редакторе</a></div></div></section>
}

function UseCases({ page }: { page: SitePage }) {
  const scenarios = pageScenarios[page.path] ?? ['Рабочий материал', 'Публикация', 'Презентация', 'Личный проект']
  const details = pageSeoDetails[page.path]
  return <section className="section surface"><div className="container"><div className="section-heading"><span className="eyebrow">Сценарии</span><h2>Где пригодится {page.navLabel.toLowerCase()}</h2><p>Выберите задачу, которая ближе к вашему проекту, и адаптируйте результат под нужный формат.</p></div><div className="use-case-layout"><div className="use-case-image"><img src={imageSource(page.image)} width="690" height="540" alt={`Варианты применения инструмента «${page.navLabel}»`} loading="lazy" decoding="async" /></div><div className="use-case-list">{scenarios.map((scenario, index) => <article key={scenario}><span>{String(index + 1).padStart(2, '0')}</span><div><h3>{scenario}</h3><p>{details.scenarioNotes[index]}</p></div></article>)}</div></div></div></section>
}

function IntentDetails({ page }: { page: SitePage }) {
  const details = pageSeoDetails[page.path]
  return <section className="section section-tint"><div className="container intent-layout"><div className="intent-intro"><span className="eyebrow">Практическая проверка</span><h2>Что важно знать о задаче «{page.navLabel}»</h2><p>Подготовьте исходные материалы и заранее определите, по каким признакам будете оценивать результат.</p></div><dl className="intent-facts"><div><dt>Что подготовить</dt><dd>{details.input}</dd></div><div><dt>Что проверить</dt><dd>{details.output}</dd></div><div><dt>Ограничения</dt><dd>{details.limitation}</dd></div></dl></div></section>
}

function FAQ({ page }: { page: SitePage }) {
  const items = getPageFaq(page)
  return <section className="section surface faq-section"><div className="container faq-grid"><div><span className="eyebrow">FAQ</span><h2>Частые вопросы</h2><p>Коротко о старте, собственных материалах и дальнейшей настройке.</p></div><div>{items.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}</div></div></section>
}

function FinalCTA({ heading = 'Начните с идеи, закончите готовым дизайном' }: { heading?: string }) {
  const previews = ['/images/hero-collage.webp', '/images/background-before-after.webp', '/images/creative-output-gallery.webp']
  return <section className="section final-cta-section"><div className="container final-cta"><div><span className="eyebrow eyebrow-light">Онлайн-редактор</span><h2>{heading}</h2><p>Выберите формат, добавьте материалы и соберите результат в одном рабочем пространстве.</p><a className="button button-light" href={CTA_URL} rel="nofollow">Создать дизайн</a></div><div className="floating-cards" aria-hidden="true">{previews.map((src) => <img src={src} key={src} alt="" loading="lazy" decoding="async" />)}</div></div></section>
}

function Homepage({ page }: { page: SitePage }) {
  return <><Hero page={page} /><ToolsMosaic /><EditorShowcase /><FormatGallery /><Steps /><BeforeAfter /><RouteMatrix /><FAQ page={page} /><FinalCTA /></>
}

function RelatedPages({ page }: { page: SitePage }) {
  return <section className="section section-tint"><div className="container"><div className="section-heading"><span className="eyebrow">Продолжить работу</span><h2>Связанные инструменты</h2><p>Перейдите к соседней задаче, если нужно дополнить или по-другому подготовить материал.</p></div><div className="related-grid">{page.related.map((path) => { const related = pageByPath.get(path); return related ? <a href={path} className="related-card" key={path}><img src={imageSource(related.image)} width="380" height="220" alt="" loading="lazy" decoding="async" /><div><h3>{related.navLabel}</h3><p>{related.description}</p><b>Открыть страницу</b></div></a> : null })}</div></div></section>
}

function FeaturePage({ page }: { page: SitePage }) {
  return <><Hero page={page} compact /><section className="section surface"><div className="container statement"><span className="eyebrow">Главное преимущество</span><h2>{page.benefit}</h2><p>Исходные материалы, рабочие инструменты и готовый макет находятся рядом — так проще сравнивать варианты и контролировать результат.</p></div></section><FeatureWorkbench page={page} /><Steps page={page} /><UseCases page={page} /><IntentDetails page={page} /><RelatedPages page={page} /><FAQ page={page} /><FinalCTA heading={page.heading} /></>
}

export default function App({ pathname }: { pathname?: string }) {
  const currentPath = pathname ?? (typeof window === 'undefined' ? '/' : window.location.pathname)
  const page = findPage(currentPath)
  useEffect(() => {
    document.documentElement.lang = 'ru'

    const root = document.documentElement
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) return

    root.classList.add('motion-ready')

    const heroCopy = document.querySelector<HTMLElement>('.hero-copy')
    const heroVisual = document.querySelector<HTMLElement>('.hero-visual')
    heroCopy?.classList.add('motion-hero-copy')
    heroVisual?.classList.add('motion-hero-visual')

    const revealSelectors = [
      '.section-heading',
      '.statement',
      '.workbench-panel',
      '.faq-grid > div:first-child',
      '.split-feature > div',
      '.intent-intro',
      '.intent-facts',
      '.editor-window',
      '.wide-gallery',
      '.split-feature > img',
      '.workbench-media',
      '.use-case-image',
      '.final-cta',
    ]
    const revealElements = Array.from(document.querySelectorAll<HTMLElement>(revealSelectors.join(',')))
    revealElements.forEach((element) => element.classList.add('motion-reveal'))

    const groups = Array.from(document.querySelectorAll<HTMLElement>('.mosaic, .step-grid, .route-grid, .tool-list, .use-case-list, .related-grid, .floating-cards'))
    groups.forEach((group) => {
      group.classList.add('motion-group')
      Array.from(group.children).forEach((child, index) => {
        const item = child as HTMLElement
        item.classList.add('motion-item')
        item.style.setProperty('--motion-delay', `${Math.min(index * 70, 280)}ms`)
      })
    })

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-visible')
        observer.unobserve(entry.target)
      })
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })

    revealElements.forEach((element) => observer.observe(element))
    groups.forEach((group) => observer.observe(group))

    const heroFrame = window.requestAnimationFrame(() => {
      heroCopy?.classList.add('is-visible')
      heroVisual?.classList.add('is-visible')
    })

    return () => {
      window.cancelAnimationFrame(heroFrame)
      observer.disconnect()
      root.classList.remove('motion-ready')
    }
  }, [page.path])
  return <><a className="skip-link" href="#content">К содержанию</a><Header /><main id="content">{page.path === '/' ? <Homepage page={page} /> : <FeaturePage page={page} />}</main><Footer /></>
}
